import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, CheckCircle2, Loader2, AlertCircle, Clock } from 'lucide-react';

/**
 * ForgotPasswordPage — Dutch UI to request a password-reset link.
 *
 * Posts to /api/auth/forgot-password with { email }. Always shows the same
 * generic success message — no account enumeration is possible from the UI.
 * Any transport error also collapses to the generic success state so a
 * silent 404 cannot reveal whether the endpoint is misconfigured.
 *
 * Feb 2026 — Adds a 5-minute client-side throttle keyed by lowercased
 * e-mail, stored in localStorage. The server enforces the same 5-min window
 * (duplicate_blocked) but the UX-throttle prevents accidental double-submits
 * and tells the user "check your inbox" instead of silently re-requesting.
 */
const THROTTLE_MINUTES = 5;
const THROTTLE_KEY = 'powersure_pwreset_last_request';

function readLastRequest(email) {
  try {
    const raw = localStorage.getItem(THROTTLE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.email !== email) return null;
    return Number(data.ts) || null;
  } catch { return null; }
}

function writeLastRequest(email) {
  try {
    localStorage.setItem(THROTTLE_KEY, JSON.stringify({
      email, ts: Date.now(),
    }));
  } catch { /* ignore quota */ }
}

function throttleSecondsLeft(email) {
  const last = readLastRequest(email);
  if (!last) return 0;
  const elapsed = (Date.now() - last) / 1000;
  const window_s = THROTTLE_MINUTES * 60;
  return Math.max(0, Math.ceil(window_s - elapsed));
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [throttleLeft, setThrottleLeft] = useState(0);

  // Recompute throttle countdown every second while throttled.
  useEffect(() => {
    const e = (email || '').trim().toLowerCase();
    if (!e) { setThrottleLeft(0); return; }
    const tick = () => setThrottleLeft(throttleSecondsLeft(e));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [email]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Vul een geldig e-mailadres in.');
      return;
    }
    const lower = email.trim().toLowerCase();
    if (throttleSecondsLeft(lower) > 0) {
      // Show the throttle banner — do NOT submit.
      setThrottleLeft(throttleSecondsLeft(lower));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        '/api/auth/forgot-password',
        { email: lower },
        { headers: { 'Content-Type': 'application/json' } }
      );
    } catch (err) {
      // Even on transport errors we show the generic message — no leakage.
    } finally {
      writeLastRequest(lower);
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-muted/30 px-4"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1
              className="text-xl font-semibold text-foreground"
              style={{ fontFamily: 'Outfit' }}
              data-testid="forgot-password-title"
            >
              Wachtwoord vergeten
            </h1>
            <p className="text-xs text-muted-foreground">Powersure account</p>
          </div>
        </div>

        {done ? (
          <div className="space-y-4" data-testid="forgot-password-success">
            <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                Als dit e-mailadres bekend is, ontvangt u binnen enkele
                minuten een resetlink. Controleer ook uw spam-map.
              </p>
            </div>
            <Link
              to="/admin/login"
              className="block text-center text-sm text-primary hover:underline"
              data-testid="forgot-password-back-to-login"
            >
              Terug naar inloggen
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
            <p className="text-sm text-muted-foreground">
              Vul uw e-mailadres in. We sturen u een link waarmee u een nieuw
              wachtwoord kunt instellen.
            </p>
            <div>
              <label htmlFor="fp-email" className="text-xs font-medium text-foreground uppercase tracking-wider">
                E-mailadres
              </label>
              <input
                id="fp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="forgot-password-input-email"
                placeholder="naam@voorbeeld.nl"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-900"
                data-testid="forgot-password-error"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {throttleLeft > 0 && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900"
                data-testid="forgot-password-throttle"
              >
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm">
                  Er is net al een resetlink verstuurd. Controleer uw inbox
                  voordat u opnieuw aanvraagt
                  {throttleLeft >= 60
                    ? ` (probeer over ${Math.ceil(throttleLeft / 60)} min opnieuw).`
                    : ` (probeer over ${throttleLeft} sec opnieuw).`}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || throttleLeft > 0}
              className="btn-primary w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-50"
              data-testid="forgot-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Bezig…' : 'Verstuur resetlink'}
            </button>

            <Link
              to="/admin/login"
              className="block text-center text-sm text-primary hover:underline pt-1"
              data-testid="forgot-password-back-to-login"
            >
              Terug naar inloggen
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
